import { Directive, OnInit, inject, signal } from "@angular/core";
import { ListStateService } from "src/app/services/list-state.service";
import { ColumnFilters } from "./column-filters";
import { Router, ActivatedRoute } from "@angular/router";
import { ConfirmationService } from "primeng/api";
import { CrudService } from "src/app/services/crud.service";

@Directive()
export abstract class AbstractListController<T, ID> extends ColumnFilters implements OnInit {
  readonly items = signal<T[]>([]);
  readonly filteredItems = signal<T[]>([]);
  item: T;
  readonly page = signal(1);
  readonly count = signal(0);
  readonly pageSize = signal(10);
  pageSizes = [5, 10, 15];
  readonly loading = signal(false);
  readonly error = signal<string>(undefined);
  readonly cols = signal<any[]>([]);
  colsView: any[] = [];
  readonly colsFilter = signal<any[]>([]);
  colsPrimaryFilter: any[] = [];
  readonly columnOptions = signal<any[]>([]);
  readonly selectedColumns = signal<any[]>([]);
  readonly filterSidebar = signal<boolean>(false);
  private readonly initialized = signal(false);
  private readonly listState = inject(ListStateService);

  private readonly loadingRequest = signal(false);

  constructor(
    protected service: CrudService<T, ID>,
    protected confirmationService: ConfirmationService,
    protected router: Router,
    protected route: ActivatedRoute,
    public path: string
  ) {
    super();
  }

  ngOnInit(): void {
    this.initializeFromUrl();
  }

  reset(): void {
    this.clearFilters();
    this.page.set(1);

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        page: this.page(),
        limit: this.pageSize(),
      },
    });

    this.loadItems();
  }

  load(): void {
    if (this.loadingRequest()) return;
    this.loadingRequest.set(true);

    const params = this.getRequestParams(this.page(), this.pageSize());

    this.service.list(params).subscribe({
      next: (data) => {
        this.items.set(data);
        this.count.set(this.service.size);
        this.applyFilters(this.getFilterParams());
        this.loading.set(false);
        this.loadingRequest.set(false);
      },
      error: (err) => {
        this.error.set(err);
        this.loading.set(false);
        this.loadingRequest.set(false);
      },
    });

    this.updateUrlParams(params);
  }

  setupDynamicFilters(): void {
    this.columnOptions.set(this.cols().map((col) => ({
      label: col.header,
      value: col,
    })));

    this.selectedColumns.set([...this.cols()]);
  }

  getFilterLabel(col: any): string {
    const val = this.filter(col.field);

    if (col.type === "select") {
      const option = col.options?.find((opt: any) => opt.value === val);
      return option ? option.label : val;
    }

    return val;
  }

  clearSingleFilter(col: any): void {
    this.setFilter(col.field, "");
    this.page.set(1);

    const queryParams: any = {
      page: this.page(),
      limit: this.pageSize(),
      ...this.getFilterParams(),
    };

    delete queryParams[col.field];

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
    });

    this.loadItems();
  }

  onPrimaryFilterChange(): void {
    this.page.set(1);

    const filters = this.getFilterParams();

    if (Object.values(filters).some((val) => val === null)) {
      this.reset();
    }

    const cleanedFilters = Object.fromEntries(
      Object.entries(filters).filter(
        ([, val]) => val !== null && val !== undefined && val !== ""
      )
    );

    const queryParams = {
      page: this.page(),
      limit: this.pageSize(),
      ...cleanedFilters,
    };

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: undefined,
    });

    this.updateUrlParams(queryParams);
    this.loadItems();
  }

  filterItem(item: T, filters: any): boolean {
    return this.cols().every((col) => {
      const key = col.field as keyof T;
      const itemValue = item[key];
      const filterValue = filters[col.field];

      if (!filterValue) return true;

      if (typeof itemValue === "string") {
        return itemValue.toLowerCase().includes(filterValue.toLowerCase());
      }

      if (typeof itemValue === "number") {
        return itemValue === +filterValue;
      }

      if (itemValue instanceof Date || typeof itemValue === "object") {
        return JSON.stringify(itemValue)
          .toLowerCase()
          .includes(filterValue.toString().toLowerCase());
      }

      return true;
    });
  }

  loadItems(): void {
    if (this.loadingRequest()) return;
    this.loadingRequest.set(true);

    const params = this.getRequestParams(this.page(), this.pageSize());

    this.service.list(params).subscribe({
      next: (data) => {
        this.items.set(data);
        this.count.set(this.service.size);
        this.applyFilters(this.getFilterParams());
        this.loading.set(false);
        this.loadingRequest.set(false);

        this.updateUrlParams(params);
      },
      error: (err) => {
        this.error.set(err);
        this.loading.set(false);
        this.loadingRequest.set(false);
      },
    });
  }

  handlePageChange(event: number): void {
    this.page.set(event);
    this.loadItems();
  }

  handlePageSizeChange(event: any): void {
    this.pageSize.set(event.target.value);
    this.page.set(1);
    this.loadItems();
  }

  getFilterParams(): any {
    const params: any = {};
    Object.entries(this.filters()).forEach(([field, value]) => {
      if (value !== "" && value !== null && value !== undefined) {
        params[field] = value;
      }
    });
    return params;
  }

  applyFilterParams(params: any): void {
    const next: Record<string, any> = {};
    Object.keys(params)
      .filter((key) => key !== "page" && key !== "limit")
      .forEach((key) => (next[key] = params[key]));
    this.filters.set(next);
  }

  getItemByField(fieldName: string, value: any, collection: any[]): any {
    return collection.find((item) => item[fieldName] === value);
  }

  resetFilters(): void {
    this.clearFilters();
    this.page.set(1);
    this.loadItems();
  }

  deleteItem(id: number): void {
    this.confirmationService.confirm({
      message: "Sei sicuro di voler eliminare questo elemento?",
      header: "Conferma eliminazione",
      icon: "pi pi-exclamation-triangle",
      accept: () => {
        this.service.delete(id).subscribe({
          next: () => this.loadItems(),
          error: (err) => (this.error.set(err)),
        });
      },
    });
  }

  onDelete(id: number): void {
    this.confirmationService.confirm({
      message: "Sei sicuro di voler eliminare questo elemento?",
      header: "Conferma eliminazione",
      icon: "pi pi-exclamation-triangle",
      accept: () => {
        this.service.delete(id).subscribe({
          next: () => this.loadItems(),
          error: (err) => (this.error.set(err)),
        });
      },
    });
  }

  protected applyFilters(filters: any): void {
    this.filteredItems.set(this.items().filter((item) =>
      this.filterItem(item, filters)
    ));
    this.paginate();
  }

  protected paginate(): void {
    this.filteredItems.set(this.filteredItems().slice(
      (this.page() - 1) * this.pageSize(),
      this.page() * this.pageSize()
    ));
  }

  protected getRequestParams(page: number, pageSize: number): any {
    return {
      page: page,
      limit: pageSize,
      ...this.getFilterParams(),
    };
  }

  protected updateUrlParams(params: any): void {
    const filters = this.getFilterParams();
    const removed: Record<string, null> = {};
    Object.keys(this.route.snapshot.queryParams)       // a filter that was cleared must leave the URL too
      .filter((key) => key !== "page" && key !== "limit" && !(key in filters))
      .forEach((key) => (removed[key] = null));
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        page: this.page(),
        limit: this.pageSize(),
        ...removed,
        ...filters,
      },
      queryParamsHandling: "merge",
    });
  }

  protected initializeFromUrl(): void {
    this.route.queryParams.subscribe((params) => {
      this.listState.set(this.path, params);
      this.page.set(+params["page"] || 1);
      this.pageSize.set(+params["limit"] || 10);
      this.applyFilterParams(params);

      if (!this.initialized()) {
        this.initialized.set(true);
        this.loadItems();
      }
    });
  }
}
